import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 90000 });

export const fetchPrices = async () => (await client.get("/prices")).data;
export const fetchTicker = async () => (await client.get("/ticker")).data;
export const fetchChart = async (symbol, days = "1") =>
  (await client.get(`/chart/${symbol}`, { params: { days } })).data;
export const fetchNews = async (group) =>
  (await client.get("/news", { params: { group } })).data;
export const fetchHotNews = async () =>
  (await client.get("/news/hot")).data;
const usableNewsText = (value) => typeof value === "string" && value.trim().length > 0;
export const localizedNews = (item, lang = "en") => ({
  ...item,
  title: lang === "id"
    ? (usableNewsText(item?.title_id) ? item.title_id : (item?.title_en || item?.title || ""))
    : (item?.title_en || item?.title || ""),
  body: lang === "id"
    ? (usableNewsText(item?.summary_id) ? item.summary_id : (item?.summary_en ?? item?.body ?? ""))
    : (item?.summary_en ?? item?.body ?? ""),
});
export const fetchLatestAnalysis = async (coin, lang = "en") =>
  (await client.get(`/analysis/latest/${coin}`, { params: { lang } })).data;
export const fetchMarketSummary = async (lang = "en") =>
  (await client.get("/market/summary", { params: { lang } })).data;
export const TRACK_RECORD_COINS = ["BTC", "ETH", "SOL", "HYPE"];
const TRACK_RECORD_STATUSES = ["tracking", "building_history"];
const TRACK_RECORD_STANCES = ["BUY_ON_CONFIRMATION", "ACCUMULATE_GRADUALLY", "HOLD", "WAIT", "WAIT_FOR_PULLBACK", "AVOID_NEW_ENTRY", "REDUCE_RISK", "INSUFFICIENT_DATA"];
const TRACK_RECORD_RISKS = ["LOW", "MODERATE", "ELEVATED", "HIGH", "INSUFFICIENT_DATA"];
const TRACK_RECORD_OUTCOMES = ["PENDING", "NOT_TRIGGERED", "OPEN", "OPEN_AT_HORIZON", "TARGET", "TARGET_HIT", "STOP", "STOP_HIT", "AMBIGUOUS", "INSUFFICIENT_DATA", "UNAVAILABLE"];
const TRACK_RECORD_REASONS = ["FULL_ENTRY_CONFIRMATION", "BREAKOUT_CONFIRMATION", "VOLUME_CONFIRMATION", "ADVERSE_MARKET_CONTEXT", "LOW_VOLUME", "FALSE_BREAKOUT_RISK", "STOP_OR_INVALIDATION", "BREAKDOWN_RISK", "SERIOUS_DOWNSIDE_CONDITION", "OVEREXTENDED_OR_AVOID_CHASING", "ACCUMULATION_ZONE_WITH_ACCEPTABLE_REGIME", "CONFLICTING_OR_ADVERSE_CONTEXT", "CONSTRUCTIVE_REGIME_WITHOUT_ENTRY", "NO_CONSTRUCTIVE_ENTRY_OR_HOLD_CONDITION", "MONITOR_EXISTING_CONDITIONS"];
const TRACK_RECORD_SUPPORTING = {
  key_condition: ["BREAKOUT_CONFIRMED", "OVEREXTENDED", "ALIGNMENT_MIXED", "MONITOR_EXISTING_CONDITIONS", "INSUFFICIENT_DATA"],
  technical_signal: ["ENTER", "WAIT", "AVOID", "SELL", "INSUFFICIENT_DATA"],
  market_regime: ["LONG_TERM_BULLISH", "LONG_TERM_BEARISH", "MIXED", "INSUFFICIENT_DATA"],
  cross_event: ["NONE", "GOLDEN_CROSS", "DEATH_CROSS", "INSUFFICIENT_DATA"],
  breakout_status: ["CONFIRMED_BREAKOUT", "UNCONFIRMED_BREAKOUT", "CONFIRMED_BREAKDOWN", "UNCONFIRMED_BREAKDOWN", "RANGE", "INSUFFICIENT_DATA"],
  multitimeframe_alignment: ["BULLISH", "BEARISH", "MIXED", "INSUFFICIENT_DATA"],
  market_context_status: ["BULLISH", "BEARISH", "NEUTRAL", "INSUFFICIENT_DATA"],
};
const trackEnum = (value, allowed) => typeof value === "string" && allowed.includes(value) ? value : null;
const trackFinite = (value) => typeof value === "number" && Number.isFinite(value) ? value : null;
const trackTimestamp = (value) => typeof value === "string" && Number.isFinite(new Date(value).getTime()) ? value : null;
const trackList = (value, allowed = null) => Array.isArray(value) ? value.map((item) => allowed ? trackEnum(item, allowed) : (typeof item === "string" && item ? item : null)).filter(Boolean) : [];

export const normalizeSignalTrackRecord = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const status = trackEnum(raw.status, TRACK_RECORD_STATUSES);
  if (!status || !Array.isArray(raw.records)) return null;
  const normalizeOutcome = (outcome) => {
    if (!outcome || typeof outcome !== "object") return null;
    return {
      evaluation_status: trackEnum(outcome.evaluation_status, TRACK_RECORD_OUTCOMES),
      outcome_classification: trackEnum(outcome.outcome_classification, TRACK_RECORD_OUTCOMES),
      horizon_at: trackTimestamp(outcome.horizon_at), evaluated_at: trackTimestamp(outcome.evaluated_at),
      evaluation_candle_open_at: trackTimestamp(outcome.evaluation_candle_open_at), evaluation_candle_close_at: trackTimestamp(outcome.evaluation_candle_close_at),
      return_pct: trackFinite(outcome.return_pct), maximum_favorable_excursion_pct: trackFinite(outcome.maximum_favorable_excursion_pct), maximum_adverse_excursion_pct: trackFinite(outcome.maximum_adverse_excursion_pct),
    };
  };
  const records = raw.records.map((record) => {
    if (!record || typeof record !== "object" || typeof record.record_id !== "string" || !record.record_id || !TRACK_RECORD_COINS.includes(record.coin) || !trackTimestamp(record.created_at)) return null;
    const outcomes = record.outcomes && typeof record.outcomes === "object" ? Object.fromEntries(["24H", "7D", "30D"].map((key) => [key, normalizeOutcome(record.outcomes[key])])) : {};
    return {
      record_id: record.record_id, coin: record.coin, created_at: trackTimestamp(record.created_at), reference_candle_at: trackTimestamp(record.reference_candle_at),
      trader_stance: trackEnum(record.trader_stance, TRACK_RECORD_STANCES), investor_stance: trackEnum(record.investor_stance, TRACK_RECORD_STANCES), risk_level: trackEnum(record.risk_level, TRACK_RECORD_RISKS),
      reference_price: trackFinite(record.reference_price), entry_price: trackFinite(record.entry_price), target_price: trackFinite(record.target_price), stop_or_invalidation_price: trackFinite(record.stop_or_invalidation_price), market_context_score: trackFinite(record.market_context_score),
      reason_codes: trackList(record.reason_codes, TRACK_RECORD_REASONS), more_constructive_if: trackList(record.more_constructive_if, TRACK_RECORD_REASONS), more_cautious_if: trackList(record.more_cautious_if, TRACK_RECORD_REASONS), invalidated_if: trackList(record.invalidated_if, TRACK_RECORD_REASONS),
      key_condition: trackEnum(record.key_condition, TRACK_RECORD_SUPPORTING.key_condition), technical_signal: trackEnum(record.technical_signal, TRACK_RECORD_SUPPORTING.technical_signal), conservative_label: trackEnum(record.conservative_label, TRACK_RECORD_STANCES), market_regime: trackEnum(record.market_regime, TRACK_RECORD_SUPPORTING.market_regime), cross_event: trackEnum(record.cross_event, TRACK_RECORD_SUPPORTING.cross_event), breakout_status: trackEnum(record.breakout_status, TRACK_RECORD_SUPPORTING.breakout_status), multitimeframe_alignment: trackEnum(record.multitimeframe_alignment, TRACK_RECORD_SUPPORTING.multitimeframe_alignment), market_context_status: trackEnum(record.market_context_status, TRACK_RECORD_SUPPORTING.market_context_status), volume_confirmed: typeof record.volume_confirmed === "boolean" ? record.volume_confirmed : null, decision_rule_version: typeof record.decision_rule_version === "string" ? record.decision_rule_version : null, outcomes,
    };
  }).filter(Boolean);
  const seenRecordIds = new Set();
  const uniqueRecords = records.filter((record) => { if (seenRecordIds.has(record.record_id)) return false; seenRecordIds.add(record.record_id); return true; });
  const summaryRaw = raw.summary && typeof raw.summary === "object" ? raw.summary : {};
  const summary = Object.fromEntries(["total_recorded_decision_events", "pending_records", "resolved_actionable_trades", "target_hits", "stop_hits", "open_at_horizon", "not_triggered", "ambiguous", "insufficient_data", "wait_hold_observations", "avoid_reduce_observations", "success_rate_denominator"].map((key) => [key, Number.isInteger(summaryRaw[key]) && summaryRaw[key] >= 0 ? summaryRaw[key] : 0]));
  summary.success_rate = typeof summaryRaw.success_rate === "number" && Number.isFinite(summaryRaw.success_rate) && summaryRaw.success_rate >= 0 && summaryRaw.success_rate <= 1 && summary.success_rate_denominator > 0 ? summaryRaw.success_rate : null;
  const paginationRaw = raw.pagination && typeof raw.pagination === "object" ? raw.pagination : {};
  const nextCursor = typeof paginationRaw.next_cursor === "string" && paginationRaw.next_cursor ? paginationRaw.next_cursor : null;
  return { status, records: uniqueRecords, summary, pagination: { limit: Number.isInteger(paginationRaw.limit) && paginationRaw.limit >= 1 && paginationRaw.limit <= 100 ? paginationRaw.limit : 10, has_more: paginationRaw.has_more === true && Boolean(nextCursor), next_cursor: paginationRaw.has_more === true ? nextCursor : null } };
};

export const fetchSignalTrackRecord = async (coin, limit = 10, cursor) => {
  const normalizedCoin = TRACK_RECORD_COINS.includes(coin) ? coin : "BTC";
  const safeLimit = Math.max(1, Math.min(100, Number.isInteger(limit) ? limit : 10));
  const params = { coin: normalizedCoin, limit: safeLimit };
  if (typeof cursor === "string" && cursor) params.cursor = cursor;
  const response = await client.get("/signal-track-record", { params });
  const normalized = normalizeSignalTrackRecord(response.data);
  if (!normalized) throw Object.assign(new Error("Malformed signal track record response"), { code: "MALFORMED_RESPONSE" });
  return normalized;
};

const MARKET_TEXT_FIELDS = ["headline", "summary", "bottom_line"];
const MARKET_LIST_FIELDS = ["drivers", "risks"];
const validMarketText = (value) => typeof value === "string" && value.trim().length > 0 ? value : null;
const normalizeMarketDoc = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const result = { ...raw };
  MARKET_TEXT_FIELDS.forEach((field) => {
    if (field in raw) result[field] = validMarketText(raw[field]);
  });
  MARKET_LIST_FIELDS.forEach((field) => {
    if (field in raw) result[field] = Array.isArray(raw[field]) ? raw[field].map(validMarketText) : null;
  });
  return result;
};

const marketVersion = (value) => {
  if (!value || typeof value !== "object") return null;
  const id = value.generation_id || value.content_hash || value.generated_at;
  return typeof id === "string" && id ? id : null;
};

/** Convert every public response to non-destructive bilingual canonical state. */
export const normalizeMarketContext = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const isLegacy = !raw.english && !raw.indonesian;
  const english = normalizeMarketDoc(raw.english || (raw.lang !== "id" && isLegacy ? raw : null));
  const indonesian = normalizeMarketDoc(raw.indonesian || (raw.lang === "id" ? raw : null));
  const base = english || indonesian || normalizeMarketDoc(raw);
  if (!base) return null;
  const fieldValue = (field, language) => {
    const source = language === "id" ? indonesian : english;
    if (source && field in source) return source[field];
    return null;
  };
  const canonical = {
    ...base,
    version: marketVersion(english) || marketVersion(indonesian),
    drivers: [],
    risks: [],
  };
  MARKET_TEXT_FIELDS.forEach((field) => {
    canonical[field] = { en: fieldValue(field, "en"), id: fieldValue(field, "id") };
  });
  MARKET_LIST_FIELDS.forEach((field) => {
    const en = Array.isArray(english?.[field]) ? english[field] : [];
    const id = Array.isArray(indonesian?.[field]) ? indonesian[field] : [];
    const length = Math.max(en.length, id.length);
    canonical[field] = length || field in (english || {}) || field in (indonesian || {}) ? Array.from({ length }, (_, index) => ({
      en: validMarketText(en[index]), id: validMarketText(id[index]),
    })) : null;
  });
  const coinFields = ["fundamental_context", "macro_news_context", "context_outlook", "context_bottom_line"];
  const coinLists = ["catalysts", "risks"];
  canonical.coins = {};
  Object.keys({ ...(english?.coins || {}), ...(indonesian?.coins || {}), ...(raw.coins || {}) }).forEach((symbol) => {
    const en = english?.coins?.[symbol] || raw.coins?.[symbol] || {};
    const id = indonesian?.coins?.[symbol] || {};
    canonical.coins[symbol] = {};
    [...coinFields, ...coinLists].forEach((field) => {
      if (coinLists.includes(field)) {
        const enValues = Array.isArray(en[field]) ? en[field] : [];
        const idValues = Array.isArray(id[field]) ? id[field] : [];
        canonical.coins[symbol][field] = Array.from({ length: Math.max(enValues.length, idValues.length) }, (_, i) => ({ en: validMarketText(enValues[i]), id: validMarketText(idValues[i]) }));
      } else canonical.coins[symbol][field] = { en: validMarketText(en[field]), id: validMarketText(id[field]) };
    });
  });
  return canonical;
};

const isUsableLocalized = (value) => typeof value === "string" && value.trim().length > 0;
export const localizedMarketField = (value, lang = "en", unavailable = "Unavailable") => {
  if (Array.isArray(value)) return value.map((item) => localizedMarketField(item, lang, unavailable)).filter(Boolean);
  if (value && typeof value === "object") {
    const selected = lang === "id" ? value.id : value.en;
    const fallback = lang === "id" ? value.en : value.id;
    return isUsableLocalized(selected) ? selected : (isUsableLocalized(fallback) ? fallback : unavailable);
  }
  return isUsableLocalized(value) ? value : unavailable;
};
export const localizedAnalysisField = localizedMarketField;

export const mergeMarketContext = (current, incoming) => {
  if (!incoming) return current;
  if (!current) return incoming;
  if (current.version && incoming.version && current.version !== incoming.version) {
    const currentAt = parseTimestamp(current.generated_at);
    const incomingAt = parseTimestamp(incoming.generated_at);
    return incomingAt !== null && (currentAt === null || incomingAt >= currentAt) ? incoming : current;
  }
  if (current.version && incoming.version && current.version === incoming.version) {
    return {
      ...current, ...incoming,
      ...Object.fromEntries(MARKET_TEXT_FIELDS.map((field) => [field, Object.fromEntries(["en", "id"].map((lang) => [lang, incoming[field][lang] || current[field][lang] || null]))])),
      drivers: incoming.drivers ? incoming.drivers.map((item, index) => ({
        en: item.en || current.drivers[index]?.en || null, id: item.id || current.drivers[index]?.id || null,
      })) : current.drivers,
      risks: incoming.risks ? incoming.risks.map((item, index) => ({
        en: item.en || current.risks[index]?.en || null, id: item.id || current.risks[index]?.id || null,
      })) : current.risks,
    };
  }
  return incoming;
};
export const fetchSchedule = async (signal) => (await client.get("/schedule", { signal })).data;

const SNAPSHOT_NUMBER_FIELDS = [
  "sma7", "sma30", "sma50", "sma200", "sma50_previous", "sma200_previous", "sma200_slope_pct",
  "rsi14", "atr14", "atr_pct", "realized_volatility_30d_daily_pct", "realized_volatility_30d",
  "realized_volatility_30d_annualized_pct", "maximum_drawdown_30d_pct", "volume", "volume_sma20",
  "volume_ratio", "support", "resistance", "breakout_level", "breakdown_level", "distance_from_sma200_pct",
];
// Phase 3A's legacy realized_volatility_30d alias is a daily percentage, matching
// realized_volatility_30d_daily_pct; the explicit annualized field is separate.

const finiteOrNull = (value) => {
  const number = typeof value === "number" ? value : (typeof value === "string" && value.trim() !== "" ? Number(value) : NaN);
  return Number.isFinite(number) ? number : null;
};
const enumOrNull = (value, allowed) => typeof value === "string" && allowed.includes(value) ? value : null;
const DIRECTIONS = ["BULLISH", "BEARISH", "MIXED", "INSUFFICIENT_DATA"];

/** Normalize the deterministic Phase 3A snapshot without inventing unavailable values. */
export const normalizeTechnicalSnapshot = (raw) => {
  if (!raw || typeof raw !== "object" || Object.keys(raw).length === 0) return null;
  const source = raw;
  const snapshot = { ...source };
  SNAPSHOT_NUMBER_FIELDS.forEach((field) => { snapshot[field] = finiteOrNull(source[field]); });
  snapshot.indicator_schema_version = typeof source.indicator_schema_version === "string" || typeof source.indicator_schema_version === "number"
    ? source.indicator_schema_version : null;
  snapshot.last_completed_candle_at = typeof source.last_completed_candle_at === "string" && Number.isFinite(new Date(source.last_completed_candle_at).getTime()) ? source.last_completed_candle_at : null;
  snapshot.completed_candle_count = finiteOrNull(source.completed_candle_count);
  snapshot.cross_event = enumOrNull(source.cross_event, ["NONE", "GOLDEN_CROSS", "DEATH_CROSS"]);
  snapshot.sma_relationship = enumOrNull(source.sma_relationship, ["SMA50_ABOVE_SMA200", "SMA50_BELOW_OR_EQUAL_SMA200", "INSUFFICIENT_DATA"]);
  snapshot.market_regime = enumOrNull(source.market_regime, ["LONG_TERM_BULLISH", "LONG_TERM_BEARISH", "MIXED", "INSUFFICIENT_DATA"]);
  snapshot.volume_confirmed = typeof source.volume_confirmed === "boolean" ? source.volume_confirmed : null;
  snapshot.low_volume_warning = typeof source.low_volume_warning === "boolean" ? source.low_volume_warning : null;
  snapshot.breakout_status = enumOrNull(source.breakout_status, ["CONFIRMED_BREAKOUT", "UNCONFIRMED_BREAKOUT", "CONFIRMED_BREAKDOWN", "UNCONFIRMED_BREAKDOWN", "RANGE", "INSUFFICIENT_DATA"]);
  snapshot.false_breakout_warning = source.false_breakout_warning && typeof source.false_breakout_warning === "object"
    ? { warning: source.false_breakout_warning.warning === true, reason: typeof source.false_breakout_warning.reason === "string" ? source.false_breakout_warning.reason : null }
    : null;
  snapshot.overextension_status = enumOrNull(source.overextension_status, ["OVEREXTENDED", "NORMAL", "INSUFFICIENT_DATA"]);
  snapshot.timeframes = source.timeframes && typeof source.timeframes === "object" ? {
    "1H": enumOrNull(source.timeframes["1H"], DIRECTIONS),
    "4H": enumOrNull(source.timeframes["4H"], DIRECTIONS),
    "1D": enumOrNull(source.timeframes["1D"], DIRECTIONS),
  } : {};
  snapshot.multitimeframe_alignment = enumOrNull(source.multitimeframe_alignment, ["BULLISH", "BEARISH", "MIXED", "INSUFFICIENT_DATA"]);
  snapshot.conservative_label = enumOrNull(source.conservative_label, ["LONG_TERM_BULLISH", "LONG_TERM_BEARISH", "MIXED_REGIME", "ACCUMULATION_ZONE", "WAIT_FOR_CONFIRMATION", "AVOID_CHASING", "OVEREXTENDED", "HIGH_RISK", "NO_TRADE", "INSUFFICIENT_DATA"]);
  snapshot.conservative_reasons = Array.isArray(source.conservative_reasons) ? source.conservative_reasons.filter((reason) => typeof reason === "string") : [];
  return snapshot;
};

export const normalizeAnalysis = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const english = raw.english && typeof raw.english === "object" ? raw.english : (!raw.lang || raw.lang === "en" ? raw : null);
  const indonesian = raw.indonesian && typeof raw.indonesian === "object" ? raw.indonesian : (raw.lang === "id" ? raw : null);
  const localized = Object.fromEntries(["good_entry_time", "technical", "technical_bottom_line"].map((field) => [field, {
    en: validMarketText(english?.[field]), id: validMarketText(indonesian?.[field]),
  }]));
  const technicalSnapshot = normalizeTechnicalSnapshot(raw.technical_snapshot || raw.tech?.technical_snapshot);
  const legacyTech = raw.tech && typeof raw.tech === "object" ? raw.tech : {};
  return {
    ...raw,
    english, indonesian, localized,
    entry_price: finiteOrNull(raw.entry_price),
    target_price: finiteOrNull(raw.target_price),
    stop_loss: finiteOrNull(raw.stop_loss),
    technical_snapshot: technicalSnapshot,
    tech: {
      ...legacyTech,
      rsi_14: finiteOrNull(legacyTech.rsi_14 ?? technicalSnapshot?.rsi14),
      ma7: finiteOrNull(legacyTech.ma7 ?? technicalSnapshot?.sma7),
      ma30: finiteOrNull(legacyTech.ma30 ?? technicalSnapshot?.sma30),
      sma50: finiteOrNull(legacyTech.sma50 ?? technicalSnapshot?.sma50),
      sma200: finiteOrNull(legacyTech.sma200 ?? technicalSnapshot?.sma200),
      support: finiteOrNull(legacyTech.support ?? technicalSnapshot?.support),
      resistance: finiteOrNull(legacyTech.resistance ?? technicalSnapshot?.resistance),
    },
  };
};

const TIMEZONE = "Asia/Ho_Chi_Minh";

export const parseTimestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatScheduleTime = (value, timezone = TIMEZONE) => {
  const timestamp = parseTimestamp(value);
  if (timestamp === null) return "--:--";
  try {
    return new Intl.DateTimeFormat([], {
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: timezone,
    }).format(new Date(timestamp));
  } catch {
    return new Intl.DateTimeFormat([], {
      hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TIMEZONE,
    }).format(new Date(timestamp));
  }
};

export const formatCountdown = (value, now = Date.now()) => {
  const timestamp = parseTimestamp(value);
  if (timestamp === null) return "--:--";
  const ms = Math.max(0, timestamp - now);
  const hh = Math.floor(ms / 3600000);
  const mm = Math.floor(ms / 60000);
  const ss = Math.floor((ms % 60000) / 1000);
  if (hh >= 1) {
    return `${String(hh).padStart(2, "0")}:${String(Math.floor((mm % 60))).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
};

export const scheduleDisplayStatus = (schedule, job, now = Date.now()) => {
  const status = schedule?.[job];
  if (!status || typeof status !== "object") return "unavailable";
  if (status.last_error || status.error) return "failed";
  if (status.is_running === true) return "updating";
  const next = parseTimestamp(status.next_update_at ?? status.next_expected_at);
  if (next === null) return "unavailable";
  if (next <= now) return "delayed";
  return formatCountdown(next, now);
};

export const normalizeSchedule = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const hasStatusShape = raw.technical || raw.market_context || raw.next_update_at || raw.last_completed_at || raw.timezone;
  if (!hasStatusShape) return null;
  const technicalRaw = raw.technical && typeof raw.technical === "object" ? raw.technical : {};
  const contextRaw = raw.market_context && typeof raw.market_context === "object" ? raw.market_context : {};
  return {
    timezone: typeof raw.timezone === "string" && raw.timezone ? raw.timezone : TIMEZONE,
    technical: {
      last_completed_at: technicalRaw.last_completed_at ?? raw.last_completed_at ?? null,
      next_update_at: technicalRaw.next_update_at ?? raw.next_update_at ?? null,
      is_running: technicalRaw.is_running === true || (technicalRaw.is_running === undefined && raw.is_running === true),
    },
    market_context: {
      last_completed_at: contextRaw.last_completed_at ?? null,
      next_update_at: contextRaw.next_update_at ?? contextRaw.next_expected_at ?? null,
      is_running: contextRaw.is_running === true,
      last_trigger_reason: contextRaw.last_trigger_reason ?? null,
      last_error: typeof contextRaw.last_error === "string" && contextRaw.last_error ? contextRaw.last_error : (typeof raw.market_context_error === "string" && raw.market_context_error ? raw.market_context_error : null),
    },
    technical_error: typeof raw.technical_error === "string" && raw.technical_error ? raw.technical_error : null,
    market_context_error: typeof raw.market_context_error === "string" && raw.market_context_error ? raw.market_context_error : null,
    last_error: typeof raw.last_error === "string" && raw.last_error ? raw.last_error : null,
  };
};

export const getCoinContext = (marketContext, symbol) => (
  marketContext?.schema_version === 2 && marketContext?.coins?.[symbol]
    ? marketContext.coins[symbol]
    : null
);

export const triggerReasonKey = (reason) => {
  const normalized = String(reason || "").toLowerCase().replace(/[-\s]+/g, "_");
  if (normalized === "scheduled") return "scheduled_update";
  if (["price_move", "price_movement", "significant_price_movement"].includes(normalized)) return "price_triggered";
  if (["headline", "high_impact_news", "news"].includes(normalized)) return "news_triggered";
  if (["schema_upgrade", "format_update"].includes(normalized)) return "schema_upgrade";
  return "reason_unavailable";
};

export const safeCitationUrls = (citations) => {
  if (!Array.isArray(citations)) return [];
  const valid = citations.filter((url) => {
    if (typeof url !== "string" || !/^https?:\/\//i.test(url.trim())) return false;
    try {
      const parsed = new URL(url.trim());
      return (parsed.protocol === "http:" || parsed.protocol === "https:") && Boolean(parsed.hostname);
    } catch {
      return false;
    }
  }).map((url) => url.trim());
  return Array.from(new Set(valid));
};

export const formatUSD = (n, digits) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "-";
  const d = digits ?? (n < 1 ? 4 : 2);
  return "$" + Number(n).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
};

// Post-process AI narrative text to add thousands separators to bare $ numbers.
// Handles: $65352.5 → $65,352.5 ; $65352 → $65,352 ; $1,234.5 stays; 65000 (no $) stays.
export const formatNumbersInText = (text) => {
  if (!text) return text;
  return text.replace(/\$(\d{4,})(\.(\d+))?/g, (_, intPart, __, frac) => {
    const withCommas = Number(intPart).toLocaleString("en-US");
    return "$" + withCommas + (frac !== undefined ? "." + frac : "");
  });
};

const INLINE_CITATION_MARKER = /\[\s*\^?\d+(?:\s*[,;]\s*\^?\d+)*\s*\]/g;

export const normalizeMarketProse = (text) => {
  if (!text) return text;
  const withoutMarkers = String(text).replace(INLINE_CITATION_MARKER, "");
  const latinOnly = Array.from(withoutMarkers).filter((char) => {
    if (!/\p{L}/u.test(char)) return true;
    return /\p{Script=Latin}/u.test(char);
  }).join("");
  return latinOnly.replace(/[ \t]{2,}/g, " ").trim();
};

export const formatCompact = (n) => {
  if (!n && n !== 0) return "-";
  return Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 2 }).format(n);
};

export const timeAgo = (unix, lang = "en") => {
  const s = Math.floor(Date.now() / 1000) - unix;
  const L = lang === "id"
    ? { s: "d lalu", m: "m lalu", h: "j lalu", d: "h lalu" }
    : { s: "s ago", m: "m ago", h: "h ago", d: "d ago" };
  if (s < 60) return `${s}${L.s === " ago" ? "s" : ""} ${L.s}`.trim();
  if (s < 3600) return `${Math.floor(s / 60)}${L.m}`;
  if (s < 86400) return `${Math.floor(s / 3600)}${L.h}`;
  return `${Math.floor(s / 86400)}${L.d}`;
};
